package com.oasisfeng.invaluable.actor

import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.NestedLinkable
import com.oasisfeng.invaluable.Quantity
import com.oasisfeng.invaluable.data.LinkProjector

/**
 * Actor-local cursor over one known commitment-chain head.
 *
 * This is local mutable state, not [Actor] identity: different devices may know different heads for
 * the same actor, and forks remain observable commitment facts.
 *
 * @property actor Actor whose local commitment head is tracked by this journal.
 * @property projector Projection scheme used to compute each appended commitment's content digest (Milestone 9).
 */
class CommitmentJournal(val actor: Actor, private val projector: LinkProjector, head: Commitment? = null) {

    init { head?.let { require(it.issuer == actor) { "Journal head belongs to another actor." } } }

    /** Current locally accepted head for [actor]'s commitment chain. */
    var head: Commitment? = head; private set

    /** Append a new commitment to [target], or create the first commitment if this journal has no head yet. */
    fun append(target: NestedLink, amount: Quantity): Commitment
    = (head?.let { Commitment(it, target, amount, projector) } ?: Commitment(actor, target, amount, projector)).also { head = it }

    /** Append a new commitment to a typed Link-backed target. */
    fun append(target: NestedLinkable, amount: Quantity): Commitment = append(target.link, amount)

    /**
     * Copy another known commitment's target and quantity into a new local journal entry.
     *
     * This is not a chain import: the replayed commitment must already belong to [actor], and the new record is
     * appended after the current local [head].
     */
    fun replay(commitment: Commitment): Commitment {
        require(commitment.issuer == actor) { "Cannot replay another actor's commitment." }
        return append(commitment.targetLink, commitment.quantity)
    }

    /** Snapshot containing the complete ancestry of the current [head], or empty when no head is known. */
    fun snapshot(): CommitmentSnapshot = CommitmentSnapshot(listOfNotNull(head))
}
